/* @flow */

import type { LeaderboardData, LeaderboardDistrictData, LeaderboardContentData } from "shop-state/types";

import type { Model } from "crustate";
import type { Response } from "./util";
import { updateData } from "crustate";

type Data =
  | { state: "LOADING" }
  | { state: "INIT", data: LeaderboardData }
  | { state: "UPDATING", data: LeaderboardData }
  | { state: "LOADED", data: LeaderboardData }
  | { state: "ERROR", error: string };

  type DistrictsData =
  | { state: "LOADING" }
  | { state: "INIT", data: ?Array<LeaderboardDistrictData> }
  | { state: "UPDATING", data: ?Array<LeaderboardDistrictData> }
  | { state: "LOADED", data: Array<LeaderboardDistrictData> }
  | { state: "ERROR", error: string };

  type ContentData =
  | { state: "LOADING" }
  | { state: "INIT", data: ?LeaderboardContentData }
  | { state: "UPDATING", data: ?LeaderboardContentData }
  | { state: "LOADED", data: LeaderboardContentData }
  | { state: "ERROR", error: string };

// Component -> State message-types
export type LeaderboardStateRequest = {
  tag: typeof LEADERBOARD_STATE_REQUEST,
  district?: string,
  fromdate?: string,
  todate?: string,
  pagesize?: number,
};

export type LeaderboardStateProps = {
  district?: string,
  fromdate?: string,
  todate?: string,
};

export type LeaderboardResponse =
  Response<typeof LEADERBOARD_RESPONSE, ?LeaderboardData>;

export type LeaderboardEffectRequest = {
  tag: typeof LEADERBOARD_REQUEST,
  type: string,
};

export type LeaderboardDistrictsStateRequest = {
  tag: typeof LEADERBOARD_DISTRICTS_STATE_REQUEST,
};
// State <-> Effect message-types
export type LeaderboardDistrictsResponse =
  Response<typeof LEADERBOARD_DISTRICTS_RESPONSE, ?Array<LeaderboardDistrictData>>;

export type LeaderboardDistrictsEffectRequest = {
  tag: typeof LEADERBOARD_DISTRICTS_REQUEST,
  type: string,
};

export type LeaderboardContentStateRequest = {
  tag: typeof LEADERBOARD_CONTENT_STATE_REQUEST,
  district?: string,
};

export type LeaderboardContentStateProps = {
  district?: string,
};

export type LeaderboardContentEffectRequest = {
  tag: typeof LEADERBOARD_CONTENT_REQUEST,
  district: number,
};

export type LeaderboardContentResponse =
  Response<typeof LEADERBOARD_CONTENT_RESPONSE, ?LeaderboardContentData>;

// Component -> State messages
const LEADERBOARD_STATE_REQUEST: "leaderboard/state/request" = "leaderboard/state/request";
const LEADERBOARD_DISTRICTS_STATE_REQUEST: "leaderboardDistricts/state/request" = "leaderboardDistricts/state/request";
const LEADERBOARD_CONTENT_STATE_REQUEST: "leaderboardContent/state/request" = "leaderboardContent/state/request";

// State <-> Effect messages
export const LEADERBOARD_RESPONSE: "leaderboard/effect/response" = "leaderboard/effect/response";
export const LEADERBOARD_REQUEST: "leaderboard/effect/request" = "leaderboard/effect/request";

export const LEADERBOARD_DISTRICTS_RESPONSE: "leaderboardDistricts/effect/response" = "leaderboardDistricts/effect/response";
export const LEADERBOARD_DISTRICTS_REQUEST: "leaderboardDistricts/effect/request" = "leaderboardDistricts/effect/request";

export const LEADERBOARD_CONTENT_RESPONSE: "leaderboardContent/effect/response" = "leaderboardContent/effect/response";
export const LEADERBOARD_CONTENT_REQUEST: "leaderboardContent/effect/request" = "leaderboardContent/effect/request";

export const getLoadLeaderboard =
(): LeaderboardStateRequest => {
  return {
    tag: LEADERBOARD_STATE_REQUEST,
  };
};

export const getLoadLeaderboardByDate =
({ fromdate, todate, district }: LeaderboardStateProps): LeaderboardStateRequest => {
  return {
    tag: LEADERBOARD_STATE_REQUEST,
    fromdate,
    todate,
    district,
  };
};

export const getLoadLeaderboardContent =
({ district }: LeaderboardContentStateProps): LeaderboardContentStateRequest => {
  return {
    tag: LEADERBOARD_CONTENT_STATE_REQUEST,
    district,
  };
};

export const getLoadLeaderboardDistricts =
(): LeaderboardDistrictsStateRequest => {
  return {
    tag: LEADERBOARD_DISTRICTS_STATE_REQUEST,
  };
};

export const LeaderboardModel: Model<Data, {
  fromdate?: string,
  todate?: string,
  district?: number,
  pagesize?: number,
},
  LeaderboardEffectRequest |
  LeaderboardResponse |
  LeaderboardStateRequest> =
{
  id: "leaderboard",
  init: ({ fromdate, todate, district, pagesize }) => {
    return updateData(
      { state: "INIT", data: {} },
      { tag: LEADERBOARD_REQUEST, fromdate, todate, district, pagesize }
    );
  },
  update: (state, msg) => {
    switch (msg.tag) {
      case LEADERBOARD_RESPONSE:
        if (msg.error) {
          return updateData({ state: "ERROR", error: msg.error });
        }

        if (msg.data) {
          return updateData({ state: "LOADED", data: msg.data });
        }

        break;

      case LEADERBOARD_STATE_REQUEST:
        if (state.state === "LOADED" || state.state === "INIT") {
          return updateData(
            { state: "UPDATING", data: state.data },
            { ...msg, tag: LEADERBOARD_REQUEST }
          );
        }

        break;
      default:
    }
  },
};

export const LeaderboardDistrictsModel: Model<DistrictsData, {},
  LeaderboardDistrictsEffectRequest |
  LeaderboardDistrictsResponse |
  LeaderboardDistrictsStateRequest> =
{
  id: "leaderboard_districs",
  init: () => {
    return updateData(
      { state: "INIT", data: [] },
      { tag: LEADERBOARD_DISTRICTS_REQUEST }
    );
  },
  update: (state, msg) => {
    switch (msg.tag) {
      case LEADERBOARD_DISTRICTS_RESPONSE:
        if (msg.error) {
          return updateData({ state: "ERROR", error: msg.error });
        }

        if (msg.data) {
          return updateData({ state: "LOADED", data: msg.data });
        }

        break;

      case LEADERBOARD_DISTRICTS_STATE_REQUEST:
        if (state.state === "LOADED" || state.state === "INIT") {
          return updateData(
            { state: "UPDATING", data: state.data },
            { ...msg, tag: LEADERBOARD_DISTRICTS_REQUEST }
          );
        }

        break;
      default:
    }
  },
};

export const LeaderboardContentModel: Model<ContentData, {
  district?: number,
},
LeaderboardContentEffectRequest |
LeaderboardContentResponse |
LeaderboardContentStateRequest> =
{
  id: "leaderboard_content",
  init: ({ district }) => {
    return updateData(
      { state: "INIT", data: {} },
      { tag: LEADERBOARD_CONTENT_REQUEST, district }
    );
  },
  update: (state, msg) => {
    switch (msg.tag) {
      case LEADERBOARD_CONTENT_RESPONSE:
        if (msg.error) {
          return updateData({ state: "ERROR", error: msg.error });
        }

        if (msg.data) {
          return updateData({ state: "LOADED", data: msg.data });
        }

        break;

      case LEADERBOARD_CONTENT_STATE_REQUEST:
        if (state.state === "LOADED" || state.state === "INIT") {
          return updateData(
            { state: "UPDATING", data: state.data },
            { ...msg, tag: LEADERBOARD_CONTENT_REQUEST }
          );
        }

        break;
      default:
    }
  },
};
